/**
 * Tenant Store
 *
 * Zustand store for managing tenant (company) state.
 * Handles tenant data, invoicing configuration, and status.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import api from '@/lib/api'
import type { Tenant, TenantInvoicingConfig } from '@/types/tenant'

// Tenant state
interface TenantState {
  tenant: Tenant | null
  invoicingConfig: TenantInvoicingConfig | null
  isLoading: boolean
  error: string | null

  // Actions
  setTenant: (tenant: Tenant) => void
  clearTenant: () => void
  fetchTenant: () => Promise<void>
  fetchInvoicingConfig: () => Promise<void>
  refreshTenant: () => Promise<void>
  toggleInvoicing: () => Promise<boolean>
  clearError: () => void
}

/**
 * Create tenant store with persistence
 */
export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      tenant: null,
      invoicingConfig: null,
      isLoading: false,
      error: null,

      /**
       * Set tenant data directly
       */
      setTenant: (tenant: Tenant) => {
        set({ tenant, error: null })
      },

      /**
       * Clear tenant data (on logout)
       */
      clearTenant: () => {
        set({
          tenant: null,
          invoicingConfig: null,
          error: null,
        })
      },

      /**
       * Fetch current tenant from server
       */
      fetchTenant: async () => {
        set({ isLoading: true, error: null })

        try {
          const response = await api.get('/tenants/current')
          set({
            tenant: response.data,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : 'Error al cargar datos de la empresa'

          set({
            tenant: null,
            isLoading: false,
            error: message,
          })
        }
      },

      /**
       * Fetch invoicing configuration
       */
      fetchInvoicingConfig: async () => {
        try {
          const response = await api.get('/invoicing/config')
          set({ invoicingConfig: response.data })
        } catch {
          // Silently fail, config might not be available
          set({ invoicingConfig: null })
        }
      },

      /**
       * Refresh tenant data from server
       */
      refreshTenant: async () => {
        const { fetchTenant, fetchInvoicingConfig } = get()
        await Promise.all([fetchTenant(), fetchInvoicingConfig()])
      },

      /**
       * Toggle invoicing enabled/disabled
       */
      toggleInvoicing: async (): Promise<boolean> => {
        try {
          const response = await api.post('/invoicing/config/toggle')
          const { enabled } = response.data

          // Update local state
          set((state) => ({
            invoicingConfig: state.invoicingConfig
              ? { ...state.invoicingConfig, enabled }
              : null,
          }))

          return enabled
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : 'Error al cambiar estado de facturación'

          set({ error: message })
          throw error
        }
      },

      /**
       * Clear any error
       */
      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'tenant-storage',
      version: 2,
      partialize: (state) => ({
        tenant: state.tenant,
        invoicingConfig: state.invoicingConfig,
      }),
      migrate: (_persistedState, _version) => {
        // v2: activity_codes changed from string[] to EconomicActivity[]
        // Clear stale data so it gets re-fetched from API
        return { tenant: null, invoicingConfig: null, isLoading: false, error: null }
      },
    }
  )
)
